import {
  Grid,
  Button,
  Dialog,
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Box,
  ImageList,
  ImageListItem,
  IconButton,
  Typography,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { PhotoLibrary, Edit, Close } from "@mui/icons-material";
import * as React from "react";
import { useEffect, useState, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { updateNewsletter as updateNewsletterApi } from "apis";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/ar";
import "dayjs/locale/en";
import "dayjs/locale/fr";

const locales = ["en", "ar", "fr"];

type LocaleKey = (typeof locales)[number];

interface Props {
  open: boolean;
  onClose: () => void;
  item: FormFields;
}

interface FormFields {
  id: number;
  year: string;
  month: string;
  contentResume: string;
  contentMovement: string;
  contentBirthday: string;
  contentEvent: string;
  contentMessage: string;
  nurseryId: number;
  images: string[];
}

const EditNewsletterDialog: React.FC<Props> = ({ open, onClose, item }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [locale, setLocale] = React.useState<LocaleKey>(i18n.language);
  const [monthSelected, setMonthSelected] = useState<string>(item.month);
  const [submitting, setSubmitting] = useState(false);
  const { register, handleSubmit } = useForm<FormFields>({
    defaultValues: item,
  });

  const [value, setDateValue] = useState<Dayjs | null>(dayjs(item.year));

  const handleDateChange = (newValue: Dayjs | null) => {
    setDateValue(newValue);
  };

  const { enqueueSnackbar } = useSnackbar();

  const [images, setImages] = useState<File[]>([]);
  const [imgs, setImgs] = useState<string[]>(item.images);

  const imageItems = useMemo(() => {
    return images.map((item, idx) => (
      <ImageListItem
        key={idx}
        onDoubleClick={() =>
          setImages((images) => [
            ...images.slice(0, idx),
            ...images.slice(idx + 1),
          ])
        }
      >
        <img src={URL.createObjectURL(item)} alt="" />
      </ImageListItem>
    ));
  }, [images]);

  const updateNewsletter: SubmitHandler<FormFields> = (data) => {
    setSubmitting(true);
    updateNewsletterApi({ infos: data, year: value as Dayjs, imgs, images })
      .then(({ data }) => {
        enqueueSnackbar(t("newsletter.updated"), { variant: "success" });
        onClose();
      })
      .catch((err) => {
        if (err?.response?.status === 400) {
          enqueueSnackbar(err.response.data.message, { variant: "error" });
        }
      })
      .finally(() => setSubmitting(false));
  };

  useEffect(() => {
    setLocale(i18n.language);
  }, [i18n, i18n.language]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: "common.white",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Edit />
          <Typography variant="h6" fontWeight={700}>
            {t("newsletter.edit")}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "common.white" }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, pt: 3 }}>
        <form
          id="newsletter-edit-form"
          onSubmit={handleSubmit(updateNewsletter)}
        >
          <Grid container spacing={1} mt={0}>
            <Grid item xs={6}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={locale}
              >
                <DatePicker
                  label={t("labels.year")}
                  views={["year"]}
                  value={value}
                  onChange={handleDateChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("labels.month")}</InputLabel>
                <Select
                  {...register("month")}
                  label={t("labels.month")}
                  fullWidth
                  value={monthSelected}
                  onChange={(e) => setMonthSelected(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {[
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December",
                  ].map((m, i) => (
                    <MenuItem key={m} value={String(i + 1).padStart(2, "0")}>
                      {t(`labels.months_name.${m}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("inputs.action_summary")}
                {...register("contentResume", { required: true })}
                fullWidth
                variant="outlined"
                multiline
                rows={5}
                margin="dense"
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                display="flex"
                flexGrow={1}
                justifyContent="space-between"
                mt={2}
                width="100%"
                flexDirection="column"
              >
                <ImageList cols={1} sx={{ maxHeight: "360px" }}>
                  {imgs !== null &&
                    imgs.length > 0 &&
                    imgs.map((image, idx) => (
                      <ImageListItem
                        key={idx}
                        onDoubleClick={() =>
                          setImgs((imgs) => [
                            ...imgs.slice(0, idx),
                            ...imgs.slice(idx + 1),
                          ])
                        }
                      >
                        <img src={image} alt="gallery-item" />
                      </ImageListItem>
                    ))}
                  {imageItems}
                </ImageList>

                <Box display="flex" justifyContent="flex-end">
                  <Button component="label">
                    <PhotoLibrary />
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      hidden
                      onChange={(e) =>
                        setImages((images) => {
                          let files = [];
                          if (e.target.files && e.target.files.length) {
                            for (
                              let i = 0;
                              i < e.target.files?.length || 0;
                              i++
                            )
                              files.push(e.target.files[i]);
                          }
                          return [...images, ...files];
                        })
                      }
                    />
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("inputs.mfriends")}
                {...register("contentMovement", { required: true })}
                fullWidth
                variant="outlined"
                multiline
                rows={2}
                margin="dense"
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("inputs.birthdays")}
                {...register("contentBirthday", { required: true })}
                fullWidth
                variant="outlined"
                multiline
                rows={2}
                margin="dense"
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("inputs.events")}
                {...register("contentEvent", { required: true })}
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                margin="dense"
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("inputs.message")}
                {...register("contentMessage", { required: true })}
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                margin="dense"
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      {/* Actions */}
      <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: 2,
            py: 1.2,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("buttons.cancel")}
        </Button>
        <Button
          form="newsletter-edit-form"
          type="submit"
          variant="contained"
          fullWidth
          disabled={submitting}
          sx={{
            borderRadius: 2,
            py: 1.2,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": { boxShadow: 2 },
          }}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("buttons.update")
          )}
        </Button>
      </Box>
    </Dialog>
  );
};

export default EditNewsletterDialog;
